import { useState } from 'react';
import { Modal } from 'components/Modal';
import { Text } from '@mantine/core';
import { Button } from 'ui/Button';
import styles from './ChangePassword.module.scss';
import { IconCheck, IconX, IconEyeOpen, IconEyeClosed } from './icons';
import classNames from 'classnames';
import { useChangePasswordMutation } from 'redux/Api/profileApi/ProfileApi';

interface ChangePasswordProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ValidationResult {
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasSpecialChar: boolean;
  hasNumber: boolean;
  hasMinLength: boolean;
  hasMaxLength: boolean;
  passwordsMatch: boolean;
  allRequirementsMet: boolean;
}

const usePasswordValidation = (
  newPassword: string,
  confirmPassword: string,
  currentPassword: string,
): ValidationResult => {
  const hasUpperCase = Boolean(/[A-Z]/.test(newPassword));
  const hasLowerCase = Boolean(/[a-z]/.test(newPassword));
  const hasSpecialChar = Boolean(/[!@#$%^&*(),.?":{}|<>]/.test(newPassword));
  const hasNumber = Boolean(/\d/.test(newPassword));
  const hasMinLength = newPassword.length >= 8;
  const hasMaxLength = newPassword.length <= 20;
  const passwordsMatch = Boolean(
    newPassword && confirmPassword && newPassword === confirmPassword,
  );

  const allRequirementsMet = Boolean(
    hasMinLength &&
      hasMaxLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasSpecialChar &&
      hasNumber &&
      passwordsMatch &&
      currentPassword,
  );

  return {
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    hasNumber,
    hasMinLength,
    hasMaxLength,
    passwordsMatch,
    allRequirementsMet,
  };
};

export const ChangePassword = ({ isOpen, onClose }: ChangePasswordProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');
  const [artificialLoading, setArtificialLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPasswords, setShowNewPasswords] = useState(false);

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const {
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    hasNumber,
    hasMinLength,
    hasMaxLength,
    passwordsMatch,
    allRequirementsMet,
  } = usePasswordValidation(newPassword, confirmPassword, currentPassword);

  const handleSubmit = async () => {
    if (isLoading || artificialLoading) return;
    setArtificialLoading(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    if (currentPassword.length === 0) {
      setOldPasswordError('Введите пароль от аккаунта');
      setArtificialLoading(false);
      return;
    } else {
      setOldPasswordError('');
    }

    if (!allRequirementsMet) {
      setError('Убедись что все требования к паролю выполнены');
      setArtificialLoading(false);
      return;
    }

    try {
      const result = await changePassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      }).unwrap();

      if (result) {
        handleClose();
      }
    } catch (error: unknown) {
      const err = error as { data?: string };
      setError(
        err.data || 'Ошибка при смене пароля. Проверь правильность текущего пароля.',
      );
    } finally {
      setArtificialLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setOldPasswordError('');
    setArtificialLoading(false);
    setShowCurrentPassword(false);
    setShowNewPasswords(false);
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
  ) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/[а-яА-ЯёЁ\s]/g, '');
    setter(sanitizedValue);
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} setOpen={handleClose}>
      <div className={styles.modalContainer}>
        <div className={styles.modalContent}>
          <Text size='xl' fw={600} mb={16}>
            Смена пароля
          </Text>

          <Text className={styles.modalLabel}>Введи пароль от аккаунта</Text>
          <div className={styles.inputWrapper}>
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => handlePasswordChange(e, setCurrentPassword)}
              placeholder='••••••••'
              className={styles.input}
              disabled={artificialLoading || isLoading}
            />
            <button
              type='button'
              className={styles.eyeButton}
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              disabled={artificialLoading || isLoading}>
              {showCurrentPassword ? (
                <IconEyeClosed size={16} color='#ADBFDF' />
              ) : (
                <IconEyeOpen size={16} color='#ADBFDF' />
              )}
            </button>
          </div>
          <Text className={styles.modalLabel}>Задай новый пароль для входа</Text>
          <div className={styles.inputWrapper}>
            <input
              type={showNewPasswords ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => handlePasswordChange(e, setNewPassword)}
              placeholder='••••••••'
              className={styles.input}
              disabled={artificialLoading || isLoading}
            />
            <button
              type='button'
              className={styles.eyeButton}
              onClick={() => setShowNewPasswords(!showNewPasswords)}
              disabled={artificialLoading || isLoading}>
              {showNewPasswords ? (
                <IconEyeClosed size={16} color='#ADBFDF' />
              ) : (
                <IconEyeOpen size={16} color='#ADBFDF' />
              )}
            </button>
          </div>
          <Text className={styles.modalLabel}>Повтори пароль</Text>
          <div className={styles.inputWrapper}>
            <input
              type={showNewPasswords ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handlePasswordChange(e, setConfirmPassword)}
              placeholder='••••••••'
              className={styles.input}
              disabled={artificialLoading || isLoading}
            />
            <button
              type='button'
              className={styles.eyeButton}
              onClick={() => setShowNewPasswords(!showNewPasswords)}
              disabled={artificialLoading || isLoading}>
              {showNewPasswords ? (
                <IconEyeClosed size={16} color='#ADBFDF' />
              ) : (
                <IconEyeOpen size={16} color='#ADBFDF' />
              )}
            </button>
          </div>
          <Button
            onClick={handleSubmit}
            // className={classNames(styles.modalButton)}
            loading={isLoading || artificialLoading}
            theme='filed'>
            Сохранить
          </Button>
          {oldPasswordError && (
            <Text c='red' size='sm' mt={16}>
              {oldPasswordError}
            </Text>
          )}
          {error && (
            <Text c='red' size='sm' mt={16}>
              {error}
            </Text>
          )}
          <div className={styles.requirements}>
            <div className={styles.requirement}>
              {hasMinLength && hasMaxLength ? (
                <IconCheck size={16} color='green' />
              ) : (
                <IconX size={16} color='red' />
              )}
              <Text size='sm' ml={8}>
                От 8 до 20 символов
              </Text>
            </div>
            <div className={styles.requirement}>
              {hasUpperCase && hasLowerCase ? (
                <IconCheck size={16} color='green' />
              ) : (
                <IconX size={16} color='red' />
              )}
              <Text size='sm' ml={8}>
                Строчная и прописная буквы латиницей
              </Text>
            </div>
            <div className={styles.requirement}>
              {hasNumber ? (
                <IconCheck size={16} color='green' />
              ) : (
                <IconX size={16} color='red' />
              )}
              <Text size='sm' ml={8}>
                Хотя бы одна цифра
              </Text>
            </div>
            <div className={styles.requirement}>
              {hasSpecialChar ? (
                <IconCheck size={16} color='green' />
              ) : (
                <IconX size={16} color='red' />
              )}
              <Text size='sm' ml={8}>
                Один спецсимвол (!, @, #)
              </Text>
            </div>
            <div className={styles.requirement}>
              {passwordsMatch ? (
                <IconCheck size={16} color='green' />
              ) : (
                <IconX size={16} color='red' />
              )}
              <Text size='sm' ml={8}>
                Пароли совпадают
              </Text>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
